<script setup lang="ts">
import { consola } from 'consola';
import { ref } from 'vue';
import { useUserProfileStore, formatSelectedDeliveryAddress } from '@entities/user';
import { useCheckoutFormStore , useCheckoutForm } from '~/features/basket/@x/order-history';

import { useCartStore } from '@entities/order';

const logger = consola.withTag('OrderVerification');

const storeUserProfile = useUserProfileStore();
const checkoutFormStore = useCheckoutFormStore();
const validationErrors = ref<string[]>([]);

const cartStore = useCartStore();
const { v$, loader, submitForm } = useCheckoutForm({
  textTitleMessage: 'Новый заказ',
  form: checkoutFormStore.OrderForm,
});

const placeOrder = async (): Promise<void> => {
  logger.log('placeOrder called');
  logger.log('Current form data:', checkoutFormStore.OrderForm);

  try {
    // Проверяем валидацию формы
    logger.log('Starting form validation...');
    v$.value.$validate();
    validationErrors.value = [];

    if (v$.value.$error) {
      // Собираем все ошибки валидации
      logger.error('Validation errors found:', v$.value.$errors);
      v$.value.$errors.forEach((error) => {
        validationErrors.value.push(`${error.$property}: ${error.$message}`);
      });
      return;
    }

    logger.log('Validation passed, calling submitForm...');

    // Вызываем submitForm из useCheckoutForm
    const result = await submitForm();

    logger.log('submitForm completed with result:', result);

    // Закрываем корзину после успешного оформления
    cartStore.closeBasket();
    logger.log('basket closed successfully');
  } catch (error: any) {
    logger.error('Error in placeOrder:', error);
    logger.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      responseDataMessage: error?.response?._data?.message,
      dataMessage: error?.data?.message,
      stack: error?.stack,
    });

    // Извлекаем понятное сообщение с сервера для пользователя
    const serverMessage = error?.response?._data?.message || error?.data?.message;
    validationErrors.value.push(serverMessage || 'Произошла ошибка при оформлении заказа');
  }
};
</script>

<template>
  <div class="OrderVerification">
    <div class="OrderVerification__grid">
      <div class="OrderVerification__text">
        <!-- Показываем ошибки валидации -->
        <div v-if="validationErrors.length" class="validation-errors bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
          <h4 class="text-red-700 dark:text-red-400 font-semibold mb-2">❌ Ошибки при оформлении заказа:</h4>
          <ul class="list-disc list-inside">
            <li v-for="(error, index) in validationErrors" :key="index" class="text-red-600 dark:text-red-300 mb-1">
              {{ error }}
            </li>
          </ul>
        </div>

        <h3>{{ storeUserProfile.getUser?.first_name || 'Гость' }}, давайте проверим заказ.</h3>
        <p v-if="checkoutFormStore.getForm.method_receipt == 'delivery'">
          <b>Способ получения заказа:</b> доставка.
          <br />
          <span v-if="checkoutFormStore.getForm.delivery_address?.address">
            <b>Адрес доставки:</b>
            {{
              formatSelectedDeliveryAddress(
                storeUserProfile.selectedDeliveryAddress,
                checkoutFormStore.getForm.delivery_address.address,
              )
            }}
          </span>
          <span v-else class="text-red-500 font-semibold">Не выбран адрес доставки !</span>
        </p>
        <p v-else-if="checkoutFormStore.getForm.method_receipt == 'takeaway'">
          <b>Способ получения заказа:</b> самовывоз.
          <br />
          <b>Адрес заведения:</b> {{
            typeof checkoutFormStore.getForm.address_institution === 'object'
              ? `${checkoutFormStore.getForm.address_institution.city} ${checkoutFormStore.getForm.address_institution.street}`
              : checkoutFormStore.getForm.address_institution
          }}
        </p>
        <p v-else>
          <b>Способ получения заказа:</b> в зале
          <br />
          <b>Адрес заведения:</b> {{
            typeof checkoutFormStore.getForm.address_institution === 'object'
              ? `${checkoutFormStore.getForm.address_institution.city} ${checkoutFormStore.getForm.address_institution.street}`
              : checkoutFormStore.getForm.address_institution
          }}
        </p>

        <p class="mt-6 text-gray-700 text-xs flex items-center gap-2">
          <span>
            ℹ️ Обратите внимание: Во все заказы включён фиксированный сервисный сбор — он помогает сделать наш сервис
            ещё лучше и быстрее.
          </span>
        </p>

        <UiButtonMain
          :loading="loader"
          class="max-h-12 mt-12"
          block
          color="red"
          label="Осталось оплатить"
          @click="placeOrder"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.OrderVerification {
  @apply p-2.5 bg-white dark:bg-gray-900;

  &__grid {
    @apply grid gap-5  md:gap-10 bg-sky-300 dark:bg-zinc-900 rounded-2xl  pb-4 px-5 md:py-8 md:pr-20 md:pl-10 max-md:min-h-[calc(100vh-20px)] max-md:items-center;
  }

  &__text {
    @apply mt-10 text-left;

    h3 {
      @apply text-2xl font-bold md:text-3xl;
      word-break: break-word;
    }

    p {
      @apply mt-8 leading-loose;
    }
  }
}
</style>
